/**
 * Belt Configuration Redux Slice
 * Manages the global belt configuration state (martial arts, colors, contrast types)
 * Fetches from Firebase /belts path matching original Marune app
 */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../index';
import { BeltConfiguration, BeltRank, SupportedColors } from '../../types/belt';
import {
  fetchBeltConfiguration,
  getDefaultBeltConfiguration,
  getBeltsForMartialArt,
  getBeltsByLevel
} from '../../services/firebase/belt-config.service';
import { CONTRAST_TYPES } from '../../utils/belt-colors';

/**
 * State interface for belt configuration
 */
interface BeltConfigState {
  // Belt data organized by martial art
  martialArts: Record<string, BeltRank[]>;
  // Display order for martial arts (from Firebase)
  orderedMA: string[];
  // Supported color values (name → hex)
  supportedColors: SupportedColors;
  // Available contrast/pattern types
  contrastTypes: string[];
  // Martial arts that use gi (affects training tracking)
  martialArtsWithGi: string[];
  // Whether data was successfully loaded from Firebase
  syncedWithDB: boolean;
  // Loading state
  isLoading: boolean;
  // Error state
  error: string | null;
}

/**
 * Initial state with defaults matching original app
 */
const initialState: BeltConfigState = {
  martialArts: {},
  orderedMA: ["I am a spectator", 'Brazilian Jiu Jitsu'],
  supportedColors: {
    white: '#ffffff',
    yellow: '#FED83A',
    orange: '#FF7728',
    green: '#289234',
    blue: '#0012B0',
    red: '#FF0011',
    purple: '#9B00FF',
    brown: '#924D20',
    black: '#000000',
    grey: '#CCCCCC',
    none: 'transparent'
  },
  contrastTypes: [...CONTRAST_TYPES],
  martialArtsWithGi: [
    'Brazilian Jiu Jitsu',
    'Ju Jitsu',
    'Judo',
    'Japanese Jiu Jitsu'
  ],
  syncedWithDB: false,
  isLoading: false,
  error: null
};

/**
 * Async thunk to fetch belt configuration from Firebase
 */
export const loadBeltConfiguration = createAsyncThunk(
  'beltConfig/load',
  async (_, { rejectWithValue }) => {
    try {
      const config = await fetchBeltConfiguration();
      return config;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to load belt configuration');
    }
  }
);

/**
 * Belt Configuration Slice
 */
const beltConfigSlice = createSlice({
  name: 'beltConfig',
  initialState,
  reducers: {
    /**
     * Set belt configuration directly (from subscription callback)
     */
    setBeltConfiguration: (state, action: PayloadAction<BeltConfiguration>) => {
      const config = action.payload;
      state.martialArts = config.martialArts;
      state.orderedMA = config.orderedMA;
      state.supportedColors = config.supportedValues.colors;
      state.contrastTypes = config.supportedValues.contrastTypes;
      state.martialArtsWithGi = config.martialArtsWithGi;
      state.syncedWithDB = config.syncedWithDB;
      state.isLoading = false;
      state.error = null;
    },

    /**
     * Reset to initial/default state
     */
    resetBeltConfiguration: (state) => {
      const defaults = getDefaultBeltConfiguration();
      state.martialArts = defaults.martialArts;
      state.orderedMA = defaults.orderedMA;
      state.supportedColors = defaults.supportedValues.colors;
      state.contrastTypes = defaults.supportedValues.contrastTypes;
      state.martialArtsWithGi = defaults.martialArtsWithGi;
      state.syncedWithDB = false;
      state.isLoading = false;
      state.error = null;
    },

    /**
     * Set loading state
     */
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    /**
     * Set error state
     */
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadBeltConfiguration.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadBeltConfiguration.fulfilled, (state, action) => {
        const config = action.payload;
        state.martialArts = config.martialArts;
        state.orderedMA = config.orderedMA;
        state.supportedColors = config.supportedValues.colors;
        state.contrastTypes = config.supportedValues.contrastTypes;
        state.martialArtsWithGi = config.martialArtsWithGi;
        state.syncedWithDB = config.syncedWithDB;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(loadBeltConfiguration.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        // Keep using default values on error
      });
  }
});

// Export actions
export const {
  setBeltConfiguration,
  resetBeltConfiguration,
  setLoading,
  setError
} = beltConfigSlice.actions;

// Export reducer
export default beltConfigSlice.reducer;

// ============ Selectors ============

/**
 * Select the entire belt config state
 */
export const selectBeltConfigState = (state: RootState): BeltConfigState =>
  state.beltConfig;

/**
 * Select all martial arts with their belt configurations
 */
export const selectMartialArts = (state: RootState): Record<string, BeltRank[]> =>
  state.beltConfig.martialArts;

/**
 * Select the ordered list of martial arts for display
 */
export const selectOrderedMartialArts = (state: RootState): string[] =>
  state.beltConfig.orderedMA;

/**
 * Select supported colors (name → hex mapping)
 */
export const selectSupportedColors = (state: RootState): SupportedColors =>
  state.beltConfig.supportedColors;

/**
 * Select available contrast types
 */
export const selectContrastTypes = (state: RootState): string[] =>
  state.beltConfig.contrastTypes;

/**
 * Select martial arts that use gi
 */
export const selectMartialArtsWithGi = (state: RootState): string[] =>
  state.beltConfig.martialArtsWithGi;

/**
 * Select whether config is synced with database
 */
export const selectIsSyncedWithDB = (state: RootState): boolean =>
  state.beltConfig.syncedWithDB;

/**
 * Select loading state
 */
export const selectBeltConfigLoading = (state: RootState): boolean =>
  state.beltConfig.isLoading;

/**
 * Select error state
 */
export const selectBeltConfigError = (state: RootState): string | null =>
  state.beltConfig.error;

/**
 * Select belts for a specific martial art
 */
export const selectBeltsForMartialArt = (
  state: RootState,
  martialArt: string
): BeltRank[] => {
  const config: BeltConfiguration = {
    martialArts: state.beltConfig.martialArts,
    orderedMA: state.beltConfig.orderedMA,
    supportedValues: {
      colors: state.beltConfig.supportedColors,
      contrastTypes: state.beltConfig.contrastTypes
    },
    martialArtsWithGi: state.beltConfig.martialArtsWithGi,
    syncedWithDB: state.beltConfig.syncedWithDB
  };
  return getBeltsForMartialArt(config, martialArt);
};

/**
 * Select adult belts for a martial art
 */
export const selectAdultBelts = (
  state: RootState,
  martialArt: string
): BeltRank[] => {
  const config: BeltConfiguration = {
    martialArts: state.beltConfig.martialArts,
    orderedMA: state.beltConfig.orderedMA,
    supportedValues: {
      colors: state.beltConfig.supportedColors,
      contrastTypes: state.beltConfig.contrastTypes
    },
    martialArtsWithGi: state.beltConfig.martialArtsWithGi,
    syncedWithDB: state.beltConfig.syncedWithDB
  };
  return getBeltsByLevel(config, martialArt, 'adult');
};

/**
 * Select junior belts for a martial art
 */
export const selectJuniorBelts = (
  state: RootState,
  martialArt: string
): BeltRank[] => {
  const config: BeltConfiguration = {
    martialArts: state.beltConfig.martialArts,
    orderedMA: state.beltConfig.orderedMA,
    supportedValues: {
      colors: state.beltConfig.supportedColors,
      contrastTypes: state.beltConfig.contrastTypes
    },
    martialArtsWithGi: state.beltConfig.martialArtsWithGi,
    syncedWithDB: state.beltConfig.syncedWithDB
  };
  return getBeltsByLevel(config, martialArt, 'junior');
};

/**
 * Check if a martial art uses gi
 */
export const selectMartialArtUsesGi = (
  state: RootState,
  martialArt: string
): boolean => {
  return state.beltConfig.martialArtsWithGi.includes(martialArt);
};

/**
 * Get color hex by name
 */
export const selectColorHex = (
  state: RootState,
  colorName: string
): string => {
  const colors = state.beltConfig.supportedColors;
  const lowerName = colorName.toLowerCase();
  return colors[lowerName] || colors.white || '#ffffff';
};
